import { debug } from "../core/config.js";
import { HttpError, ResponseCache, type DataSource } from "../core/datasource.js";
import type { ListConfig, ListResponse, ListState, SearchResponse } from "../core/types.js";
import { normalizeSearchResult } from "./normalize.js";

/**
 * Global site search over `GET /:siteId/search` — one ranked result set across
 * every enabled collection and the static pages, rather than a per-collection
 * list. The endpoint takes only q/limit/offset, so no filter/sort/locale/facet
 * params exist here; and it serves `fieldData` nested, so each result is
 * flattened (normalizeSearchResult) into the exact `Item` shape lists produce —
 * everything above this class, bindings included, sees one contract.
 */
export class SearchDataSource implements DataSource {
  private readonly cache = new ResponseCache();
  private inFlight: AbortController | null = null;

  constructor(private readonly config: ListConfig) {}

  private url(state: ListState, offset: number, limit: number): string {
    const params = new URLSearchParams();
    params.set("q", state.q);
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    if (this.config.expand.length) params.set("expand", this.config.expand.join(","));
    // Same deterministic staging/production pick as the list endpoint.
    params.set("host", window.location.hostname);

    const { endpoint, siteId } = this.config;
    return `${endpoint}/${encodeURIComponent(siteId)}/search?${params}`;
  }

  async query(state: ListState, offset: number, limit: number): Promise<ListResponse> {
    const url = this.url(state, offset, limit);

    const cached = this.cache.get(url);
    if (cached) {
      debug("cache hit", url);
      return cached;
    }

    this.inFlight?.abort();
    const controller = new AbortController();
    this.inFlight = controller;

    debug("fetch", url);
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { accept: "application/json" },
    });

    if (!response.ok) {
      throw new HttpError(
        response.status,
        response.status === 404
          ? `Search is not available for site ${this.config.siteId}`
          : response.status === 402
            ? `Global search is not included in this site's Slater plan (402)`
            : `Request failed with ${response.status}`,
      );
    }

    const body = (await response.json()) as SearchResponse;
    const mapped: ListResponse = {
      success: body.success,
      collection: "search",
      count: body.count,
      total: body.total,
      limit: body.limit,
      offset: body.offset,
      items: body.results.map(normalizeSearchResult),
      ...(body.warnings ? { warnings: body.warnings } : {}),
    };
    this.cache.set(url, mapped);
    return mapped;
  }

  abort(): void {
    this.inFlight?.abort();
    this.inFlight = null;
  }
}
